import { useMemo } from 'react';
import styled from 'styled-components';
import { localizeDate } from '../../utils/datetime';
import { FlexColumn } from '../Flex/FlexColumn';
import { componentToken } from '../../style/utils';

const Wrapper = styled(FlexColumn)<{
  $padding: string | undefined;
  $hideBorder: boolean | undefined;
}>`
  width: 100%;
  border-radius: ${componentToken('table.wrapper.borderRadius')};
  ${({ theme, $hideBorder }) =>
    !!$hideBorder &&
    `border: 1px solid ${theme.tokens.components.table.wrapper.border}`};
  background-color: ${componentToken('table.wrapper.backgroundColor')};
  padding: ${({ $padding }) =>
    $padding ?? componentToken('table.wrapper.defaultPadding')};
  overflow: auto;
`;

const Table = styled.table`
  overflow-y: auto;
  max-height: 280px;
  border-collapse: collapse;

  & th,
  & td {
    border: ${({ theme }) =>
      `1px solid ${theme.tokens.components.table.root.cellBorder}`};
    padding: ${componentToken('table.root.cellPadding')};
    font-weight: ${componentToken('table.root.cellFontWeight')};
    font-size: ${componentToken('table.root.cellFontSize')};
  }

  & th {
    color: ${componentToken('table.root.headerColor')};
    text-align: left;
  }
`;

type Column = { id: string; display_name: string; type?: string };

type Props = {
  data: { [k: string]: string | number | boolean | object }[];
  columns?: Column[];
  padding?: string;
  hideBorder?: boolean;
};

const CondensedTable = ({ data, columns, padding, hideBorder }: Props) => {
  const columnsMap: Column[] = useMemo(
    () =>
      columns ??
      Array.from(
        new Set(
          data?.reduce?.(
            (acc: any, item: any) => [...acc, ...Object.keys(item || {})],
            [],
          ),
        ),
      ).map((key: string) => ({ id: key, display_name: key })),
    [columns, data],
  );

  if (data.length === 0) {
    return null;
  }

  return (
    <Wrapper $padding={padding} $hideBorder={hideBorder}>
      {columnsMap?.length > 0 && (
        <Table>
          <thead>
            <tr role="row">
              {columnsMap.map((column) => (
                <th role="columnheader" key={column.id}>
                  {column.display_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr role="row" key={idx}>
                {columnsMap.map((column, colIdx) => (
                  <td role="cell" key={`${idx}_${column.id}`}>
                    {columnsMap[colIdx]?.type === 'date'
                      ? localizeDate(row[column.id]?.toString()).format('L LT')
                      : row[column.id]?.toString() || ' '}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  );
};

export default CondensedTable;
